import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ProductAccessRowConfig } from '../components/InviteTray/ProductAccessRow';
import { PRODUCTS } from '../components/InviteTray/productCatalog';
import type { MemberRole, MemberRoleMap } from '../utils/constants';
import { withImpliedRoles } from '../utils/roleHierarchy';

import { getDefaultRoleValues, getProductKey } from './useRoles';

export type RoleSelections = Record<string, MemberRole[]>;

const PRODUCT_KEYS = new Set(PRODUCTS.map((p) => p.productKey));

export function buildSelections(roles: MemberRole[]): RoleSelections {
  const result: RoleSelections = {};
  for (const product of PRODUCTS) {
    result[product.productKey] = [];
  }
  for (const role of roles) {
    const key = getProductKey(role);
    if (PRODUCT_KEYS.has(key)) {
      result[key].push(role);
    }
  }
  return result;
}

export function flattenSelections(selections: RoleSelections): MemberRole[] {
  return PRODUCTS.flatMap((p) => selections[p.productKey] ?? []);
}

export function getSelections(selections: RoleSelections): MemberRole[] {
  return flattenSelections(selections);
}

export function selectionsEqual(a: RoleSelections, b: RoleSelections): boolean {
  return PRODUCTS.every((p) => {
    const left = new Set(a[p.productKey] ?? []);
    const right = new Set(b[p.productKey] ?? []);
    if (left.size !== right.size) return false;
    for (const role of left) {
      if (!right.has(role)) return false;
    }
    return true;
  });
}

/**
 * Determines which roles to assign when a product checkbox is checked.
 * Priority: existing selection > saved baseline > default role for product.
 */
function resolveRolesOnCheck(
  current: MemberRole[],
  baseline: MemberRole[],
  defaultRoleValues: MemberRole[],
): MemberRole[] | null {
  if (!defaultRoleValues.length && !baseline.length) return null;
  if (current.length) return null;
  if (baseline.length) return baseline;
  return defaultRoleValues;
}

type UseMemberEditParams = {
  memberRoles: MemberRole[];
  rolesByProduct: Record<string, MemberRoleMap[]>;
  /**
   * The full role map (assignable + unassignable), used to derive the member's
   * unassignable roles. Those are seeded into `selections` (so they render
   * checked) but have no toggle; since they sit in both initial and current
   * selections they don't affect `hasChanges`.
   */
  roleMap: Record<MemberRole, MemberRoleMap>;
  /**
   * IDFE-4045 (t0018_4fba688a): roles are hierarchical, so the member's held
   * roles are seeded with the roles they imply (Admin also checks Viewer).
   */
  isHierarchicalRolesTreatment?: boolean;
};

type UseMemberEditReturn = {
  selections: RoleSelections;
  rows: ProductAccessRowConfig[];
  hasChanges: boolean;
  handleCancel: () => void;
};

export function useMemberEdit({
  memberRoles,
  rolesByProduct,
  roleMap,
  isHierarchicalRolesTreatment = false,
}: UseMemberEditParams): UseMemberEditReturn {
  const { formatMessage } = useIntl();

  // The member's unassignable roles, grouped by product, folded into each row's
  // roleOptions as checked + disabled. Derived from the member's own roles, so
  // only roles they actually hold surface as locked entries.
  const unassignableRolesByProduct = useMemo(() => {
    const result: Record<string, MemberRoleMap[]> = {};
    for (const role of memberRoles) {
      const definition = roleMap[role];
      if (definition && !definition.assignable) {
        (result[definition.productKey] ??= []).push(definition);
      }
    }
    return result;
  }, [memberRoles, roleMap]);

  const initialSelections = useMemo(() => {
    const held = buildSelections(memberRoles);
    if (!isHierarchicalRolesTreatment) return held;
    return Object.fromEntries(
      Object.entries(held).map(([productKey, roles]) => [
        productKey,
        withImpliedRoles(roles, rolesByProduct[productKey] ?? []),
      ]),
    );
  }, [isHierarchicalRolesTreatment, memberRoles, rolesByProduct]);
  const [selections, setSelections] = useState(initialSelections);

  useEffect(() => {
    setSelections(initialSelections);
  }, [initialSelections]);

  const hasChanges = useMemo(
    () => !selectionsEqual(initialSelections, selections),
    [initialSelections, selections],
  );

  const handleCheckedChange = useCallback(
    (productKey: string, isChecked: boolean) => {
      setSelections((prev) => {
        if (!isChecked) {
          return { ...prev, [productKey]: [] };
        }

        const roles = resolveRolesOnCheck(
          prev[productKey] ?? [],
          initialSelections[productKey] ?? [],
          getDefaultRoleValues(rolesByProduct, productKey),
        );

        return roles ? { ...prev, [productKey]: roles } : prev;
      });
    },
    [rolesByProduct, initialSelections],
  );

  const handleRoleToggle = useCallback(
    (productKey: string, role: MemberRole, isSelected: boolean) => {
      setSelections((prev) => {
        const current = prev[productKey] ?? [];

        if (isSelected) {
          return current.includes(role) ? prev : { ...prev, [productKey]: [...current, role] };
        }

        return { ...prev, [productKey]: current.filter((v) => v !== role) };
      });
    },
    [],
  );

  const handleCancel = useCallback(() => setSelections(initialSelections), [initialSelections]);

  const rows = useMemo<ProductAccessRowConfig[]>(
    () =>
      PRODUCTS.filter(
        (product) =>
          (rolesByProduct[product.productKey]?.length ?? 0) > 0 ||
          (unassignableRolesByProduct[product.productKey]?.length ?? 0) > 0,
      ).map((product) => ({
        productKey: product.productKey,
        iconName: product.iconName,
        iconBackground: product.iconBackground,
        title: formatMessage(product.trayTitleMessage),
        description: formatMessage(product.descriptionMessage),
        hideIcon: Boolean(product.hideIconInTray),
        // Defaults derive from the assignable-only `rolesByProduct`, so an
        // unassignable role is never auto-selected. (The popover sorts assignable
        // roles first, so concat order here doesn't matter.)
        roleOptions: [
          ...(rolesByProduct[product.productKey] ?? []),
          ...(unassignableRolesByProduct[product.productKey] ?? []),
        ],
        selectedRoleValues: selections[product.productKey] ?? [],
        onCheckedChange: (isChecked: boolean) => handleCheckedChange(product.productKey, isChecked),
        onRoleToggle: (role: MemberRole, isSelected: boolean) =>
          handleRoleToggle(product.productKey, role, isSelected),
      })),
    [
      selections,
      formatMessage,
      handleCheckedChange,
      handleRoleToggle,
      rolesByProduct,
      unassignableRolesByProduct,
    ],
  );

  return { selections, rows, hasChanges, handleCancel };
}
